import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NetworkManager } from 'src/app/managers/network/network.manager';

interface CsrfInfo {
  headerName: string;
  parameterName: string;
  token: string;
}

/**
 * Chiama sb_authproxy (auth.codaq.it), non apiwebapp. withCredentials: true e' obbligatorio,
 * altrimenti il browser non manda/riceve il cookie di sessione di authproxy (cross-subdomain).
 *
 * authproxy ha CSRF protection attiva (CookieCsrfTokenRepository) su tutte le POST; cqwebapp non
 * ha HttpClientXsrfModule configurato (solo HttpClientModule liscio), quindi il token va letto
 * a mano da GET /auth/csrf prima di ogni chiamata che scrive stato.
 */
@Injectable({
  providedIn: 'root',
})
export class SsoRequest {
  constructor(private network: NetworkManager) {}

  private async csrf(): Promise<CsrfInfo> {
    const url = this.network.AuthApi() + 'auth/csrf';
    return this.network
      .http()
      .get<CsrfInfo>(url, { withCredentials: true })
      .toPromise();
  }

  /**
   * Avvia il login OIDC sul tenant indicato. Risposta: { action, redirectUrl, transactionId }
   * (o equivalente) — il chiamante deve fare window.location.href = redirectUrl.
   */
  async start(tenantId: string, returnUrl: string) {
    const info = await this.csrf();
    const url = this.network.AuthApi() + 'auth/login/start';
    const headers = { [info.headerName]: info.token };

    return this.network
      .http()
      .post(url, { tenantId, returnUrl }, { withCredentials: true, headers })
      .toPromise();
  }

  /**
   * Identita' della sessione authproxy corrente, se esiste (401 se non c'e' nessuna sessione
   * attiva o e' scaduta — il chiamante lo tratta come "nessuna sessione", non come errore).
   */
  async profile() {
    const url = this.network.AuthApi() + 'auth/profile';
    return this.network.http().get(url, { withCredentials: true }).toPromise();
  }

  /**
   * Invalida la sessione authproxy corrente (non tocca la sessione cqwebapp/legacy). returnUrl e'
   * obbligatorio (validato come il login, solo origin — non serve essere esattamente in whitelist
   * sul path). Risposta: { status, redirectUrl? } — redirectUrl, se presente, va visitato con una
   * navigazione reale (non fetch) per chiudere la sessione anche sul provider esterno.
   */
  async logout(tenantId: string, returnUrl: string) {
    const info = await this.csrf();
    const url = this.network.AuthApi() + 'auth/logout';
    const params = new HttpParams()
      .set('tenantId', tenantId)
      .set('returnUrl', returnUrl);
    const headers = { [info.headerName]: info.token };

    return this.network
      .http()
      .post(url, null, { withCredentials: true, params, headers })
      .toPromise();
  }

  /**
   * Da chiamare sulla pagina di ritorno dopo il login OIDC: authproxy risolve l'utente gia'
   * in sessione e ritorna un token legacy pronto per apiwebapp, stessa shape di PUT /login.
   */
  async legacyLogin(targetApp: string, forced: boolean = false) {
    const info = await this.csrf();
    const url = this.network.AuthApi() + 'auth/legacy-login';
    const params = new HttpParams()
      .set('targetApp', targetApp)
      .set('forced', String(forced));
    const headers = { [info.headerName]: info.token };

    return this.network
      .http()
      .post(url, null, { withCredentials: true, params, headers })
      .toPromise();
  }
}
